<script>
export default {
  created() {
    this.jobsLoading = [];
  },
  beforeDestroy() {
    this.cancelLoading = true;

    if (this.cancelReload) {
      this.cancelReload();
    }
  },
  methods: {
    /**
     * Loads jobs into the 'current' stack of the jobs store.
     * Reloads automatically with the given params if something in the jobs store got changed.
     * Should be called only once.
     *
     * @param {String} path The api path to load the jobs from.
     * @param {Object} params Params used for the api query (POST data). The keys 'filter' and 'search' will also be used if the jobs doesn't get loaded from the api, but from the client db storage (See jobs store module).
     * @param {Boolean} isFiltered If there are (not just the default) filters set in the params.
     * @param {Boolean} [isReload=false] Internally used - if the call is a reload
     * @returns {Promise} A promise which will resolve with the loaded jobs
     */
    async loadJobs(path, params, isFiltered = false, isReload = false) {
      // get list of currently already running loading processes
      const alreadyRunningLoadings = this.jobsLoading.slice(0);

      // add promise for this loading process to the list of loading processes
      let resolveLoading = null;
      this.jobsLoading.push(
        new Promise((resolve) => {
          resolveLoading = resolve;
        })
      );

      // wait for currently running loading processes to finish
      await Promise.all(alreadyRunningLoadings);

      if (this.cancelLoading) {
        resolveLoading();
        return null;
      }

      if (this.cancelReload) {
        // cancel pending reloads
        this.cancelReload();
      }
      const firstLoadIOS = this.$store.getters["isFirstLoadIOS"];
      if (!this.loadedOnce) {
        if (firstLoadIOS) {
          let ver = this.iOSversion();
          let verisonBool = true;
          if (ver && ver.length > 2 && ver[0] >= 14) {
            if (ver[0] == 14 && ver[1] < 6) {
              verisonBool = false;
            }
            if (verisonBool) {
              this.$store.dispatch("setFirstLoadIOS", false);
              location.reload();
              return null;
            }
          }
        }

        // if this is the first load we'll wait until the notifications got loaded and handled since they may add job data to the store
        await setTimeout(() => {
          this.app.layout.notificationsLoadedAndHandled;
        }, 5000);
        this.loadedOnce = true;
      }

      // get promise which will resolve after our jobs got loaded, commited and saved persistent
      const commited = this.$store.saved("jobs", null, "loadCurrent");

      const firstLoad = this.$store.getters["isFirstLoad"];

      // load jobs into 'current'-stack and get promise for it
      const loaded = this.$store.dispatch("jobs/load", {
        path,
        params,
        isFiltered,
        targetStack: "current",
        commitPayload: "loadCurrent", // set commit payload for identifying this commit on the store saved event
        sources: isReload ? ["stacks", "db"] : ["api", "stacks", "db"], // if this is a reload, we don't want to fetch from the api since a (by the store triggered) reload means that the new data is already within the store
        disableLoadDocs: firstLoad,
      });
      if (firstLoad) {
        this.$store.dispatch("setFirstLoad", false);
      }

      commited.then(async () => {
        // after loading and commiting is finished we can start waiting for a need to reload (or a cancellation):

        // create promise for cancel reloading
        const reloadCancelled = new Promise((resolve) => {
          // set the cancel reload method to the resolver of the cancel reload promise
          this.cancelReload = () => {
            resolve(false);
          };
        });

        // resolve current loading process
        resolveLoading();

        while (true) {
          // wait until the jobs store gets updated or the reload gets cancelled (= resolver of cancel reload promise gets called)
          if (
            await Promise.race([this.$store.saved("jobs"), reloadCancelled])
          ) {
            // the reloadCancelled promise always resolves with false, so this happens only if the store-saved-promise resolves first

            if (this.$store.state.jobs.persistPut.length) {
              // only do reload if there has been jobs added or changed
              // .. if not, the waiting-loop will continue and we'll wait for the next store change or a reload cancellation

              // now reload with the same params since there may be new suitable jobs in the store
              this.loadJobs(path, params, isFiltered, true);

              // break the waiting loop
              break;
            }
          } else {
            // break the waiting loop if reloading got cancelled
            break;
          }
        }
      });

      return loaded;
    },
    iOSversion() {
      if (/iP(hone|od|ad)/.test(navigator.platform)) {
        // supports iOS 2.0 and later: <http://bit.ly/TJjs1V>
        var v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);
        return [
          parseInt(v[1], 10),
          parseInt(v[2], 10),
          parseInt(v[3] || 0, 10),
        ];
      }
    },
  },
};
</script>